import {
  Chip,
  DropdownMenu,
  Icon,
  SvgIconNames,
  TooltipTrigger,
} from '@design';
import { Option } from '@terminal/components/filter/Option';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useReasonerParams } from '@terminal/views/alfred/research/hooks/useReasonerParams';
import { SubmittedAnalysisMode } from '@terminal/views/alfred/research/services/conversations-service';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import clsx from 'clsx';
import React, { RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AnalysisModeDropdown.module.scss';

interface DropdownOption {
  key: string;
  icon: SvgIconNames;
  name: string;
  value: {
    analysis_mode: SubmittedAnalysisMode;
  };
  description: string;
}

export interface AnalysisModeDropdownProps {
  isMessagePending: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
}

export const AnalysisModeDropdown = ({
  isMessagePending,
  contentRef,
}: AnalysisModeDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const { conversationId } = useReasonerParams();

  const { analysisMode, webSearch, activeConversation, setAnalysisMode } =
    useChat(state => ({
      analysisMode: state.analysisMode,
      webSearch: state.webSearch,
      activeConversation: state.getActiveConversation(conversationId),
      setAnalysisMode: state.setAnalysisMode,
    }));
  const { trackAlfredDeepSelected } = usePosthogEvents();

  const renderCustomFilterOptionLabel = (
    filterOption: FilterOption,
    disabled: boolean
  ) => {
    return (
      <div className={styles.optionWrapper}>
        {filterOption.icon && (
          <Icon
            iconName={filterOption.icon}
            size={16}
            fillColor={
              disabled ? 'var(--text-disabled)' : 'var(--text-default)'
            }
          />
        )}
        <span className={styles.label}>{filterOption.name}</span>
      </div>
    );
  };

  const options: DropdownOption[] = [
    {
      key: 'auto',
      icon: 'QuickAnalysis',
      name: t('chat:analysisMode.autoTitle'),
      value: {
        analysis_mode: 'Auto',
      },
      description: t('chat:analysisMode.autoDescription'),
    },
    {
      key: 'deep',
      icon: 'Alfred',
      name: t('chat:analysisMode.deepTitle'),
      value: {
        analysis_mode: 'Deep',
      },
      description: t('chat:analysisMode.deepDescription'),
    },
  ];

  const selectedOption = (() => {
    let option = options.find(i => i.value.analysis_mode === analysisMode);

    const lastTask = activeConversation?.tasks?.at(-1);

    if (lastTask?.submittedAnalysisMode && isMessagePending) {
      option = options.find(
        i => i.value.analysis_mode === lastTask.submittedAnalysisMode
      );
    }

    return option;
  })();

  const forceToDeep = webSearch === 'block';

  useEffect(() => {
    if (forceToDeep) {
      setAnalysisMode('Deep');
    }
  }, [webSearch]);

  return (
    <DropdownMenu
      className={styles.styledDropdownMenu}
      contentRef={contentRef}
      triggerProps={{
        disabled: isMessagePending,
      }}
      triggerNode={
        <div>
          <Chip
            className={styles.styledChip}
            label={selectedOption?.name}
            leftIcon={selectedOption?.icon}
            rightIcon="ChevronLightDown"
            rightIconSize={8}
            disabled={isMessagePending}
            prefixNode={
              <span className={styles.prefixText}>
                {t('chat:analysisMode.prefix')}
              </span>
            }
            data-testid="analysis-mode-dropdown-trigger"
          />
        </div>
      }
    >
      {options.map(i => {
        const autoDisabled = forceToDeep && i.value.analysis_mode === 'Auto';

        return (
          <TooltipContainer
            key={i.key}
            label={t('chat:analysisMode.enableAuto')}
            trigger={[TooltipTrigger.Hover]}
            inPortal
            disabled={!autoDisabled}
          >
            <div
              className={clsx({
                [styles.styledOptionWrapperDisabled]: autoDisabled,
              })}
            >
              <Option
                option={i}
                isChecked={analysisMode === i.value.analysis_mode}
                disabled={autoDisabled}
                indentedDescription={24}
                handleClick={() => {
                  setAnalysisMode(i.value.analysis_mode);

                  if (i.value.analysis_mode === 'Deep') {
                    trackAlfredDeepSelected(POSTHOG_TRACKING_SOURCES.ALFRED);
                  }
                }}
                renderCustomFilterOptionLabel={() =>
                  renderCustomFilterOptionLabel(i, autoDisabled)
                }
              />
            </div>
          </TooltipContainer>
        );
      })}
    </DropdownMenu>
  );
};
